import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { useAuth } from '../context/AuthContext';
import api, { shopifyAPI, notificationAPI } from '../lib/api';
import { toast } from 'sonner';
import { 
    CheckCircle, 
    XCircle, 
    Copy, 
    ExternalLink, 
    ShoppingBag, 
    Webhook,
    RefreshCw,
    Loader2,
    Bell,
    Clock,
    Moon,
    Users,
    Save,
    ArrowUp,
    ArrowDown,
    Play
} from 'lucide-react';
import PushNotificationsCard from '../components/PushNotificationsCard';
import NotificationToneSettings from '../components/NotificationToneSettings';
import ReassignmentChainSettings from '../components/ReassignmentChainSettings';

// Component for users to manage their own notification settings (simplified for vendors)
function MyNotificationsSection({ user }) {
    const { refreshUser } = useAuth();
    const [notificationsEnabled, setNotificationsEnabled] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (user && !loaded) {
            // Check if either notification type is enabled
            const isEnabled = user.telegram_notifications_enabled !== false;
            setNotificationsEnabled(isEnabled);
            setLoaded(true);
        }
    }, [user, loaded]);

    const handleToggle = async (enabled) => {
        setNotificationsEnabled(enabled);
        setSaving(true);
        try {
            // Update both notification types at once
            await api.put(`/users/${user.id}`, {
                telegram_notifications_enabled: enabled,
            });
            toast.success(enabled ? 'Notifications activées' : 'Notifications désactivées');
            
            if (refreshUser) {
                refreshUser();
            }
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
            setNotificationsEnabled(!enabled); // Revert on error
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                    <Bell className="w-5 h-5 text-blue-500" />
                    Notifications
                </CardTitle>
                <CardDescription>
                    Recevez des alertes pour les nouvelles commandes
                </CardDescription>
            </CardHeader>
            <CardContent>
                <div className="flex items-center justify-between p-4 rounded-lg bg-slate-50 border">
                    <div className="space-y-1">
                        <p className="font-medium">Recevoir les notifications</p>
                        <p className="text-sm text-muted-foreground">
                            {notificationsEnabled 
                                ? "Vous recevez les alertes de nouvelles commandes"
                                : "Vous ne recevez pas d'alertes"
                            }
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        {saving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                        <Switch
                            checked={notificationsEnabled}
                            onCheckedChange={handleToggle}
                            disabled={saving}
                        />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function Settings() {
    const { user } = useAuth();
    const [shopifyStatus, setShopifyStatus] = useState({ configured: false, shop_url: '' });
    const [shopifyLoading, setShopifyLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);

    // Notification settings state
    const [notifSettings, setNotifSettings] = useState({
        escalation_enabled: true,  // Toggle for escalation system
        night_mode_enabled: true,  // Toggle for night mode
        sms_confirmation_enabled: true,  // Toggle for SMS confirmation system
        escalation_delay_minutes: 5,
        night_start_hour: 23,
        night_end_hour: 6,
        notify_admin_after_all: true,
        admin_telegram_chat_id: '',
        notification_channel: 'telegram'
    });
    const [notifLoading, setNotifLoading] = useState(true);
    const [notifSaving, setNotifSaving] = useState(false);
    
    // Telegram bot status
    const [telegramStatus, setTelegramStatus] = useState(null);
    const [telegramLoading, setTelegramLoading] = useState(true);
    const [testingTelegram, setTestingTelegram] = useState(false);
    
    // Vendor priority state
    const [vendors, setVendors] = useState([]);
    const [vendorsLoading, setVendorsLoading] = useState(true);
    const [savingPriorities, setSavingPriorities] = useState(false);

    // Notification queue state
    const [queue, setQueue] = useState([]);
    const [queueLoading, setQueueLoading] = useState(false);
    const [processingQueue, setProcessingQueue] = useState(false);

    // Webhook URL for Zapier
    const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhook/zapier/order`;

    // Check if user is admin or super_admin
    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

    useEffect(() => {
        checkShopifyStatus();
        if (isAdmin) {
            fetchNotificationSettings();
            fetchVendorsPriority();
            fetchQueue();
            fetchTelegramStatus();
        }
    }, [isAdmin]);

    const checkShopifyStatus = async () => {
        try {
            const response = await shopifyAPI.getStatus();
            setShopifyStatus(response.data);
        } catch (error) {
            console.error('Error checking Shopify status:', error);
        } finally {
            setShopifyLoading(false);
        }
    };

    const fetchTelegramStatus = async () => {
        try {
            const response = await notificationAPI.getTelegramStatus();
            setTelegramStatus(response.data);
        } catch (error) {
            console.error('Error fetching Telegram status:', error);
        } finally {
            setTelegramLoading(false);
        }
    };

    const fetchNotificationSettings = async () => {
        try {
            const response = await notificationAPI.getSettings();
            setNotifSettings(response.data);
        } catch (error) {
            console.error('Error fetching notification settings:', error);
        } finally {
            setNotifLoading(false);
        }
    };

    const fetchVendorsPriority = async () => {
        try {
            const response = await notificationAPI.getVendorsPriority();
            setVendors(response.data);
        } catch (error) {
            console.error('Error fetching vendors priority:', error);
        } finally {
            setVendorsLoading(false);
        }
    };

    const fetchQueue = async () => {
        setQueueLoading(true);
        try {
            const response = await notificationAPI.getQueue();
            setQueue(response.data);
        } catch (error) {
            console.error('Error fetching queue:', error);
        } finally {
            setQueueLoading(false);
        }
    };

    const handleShopifySync = async () => {
        setSyncing(true);
        try {
            const response = await shopifyAPI.sync();
            toast.success(response.data.message || 'Synchronisation réussie');
        } catch (error) {
            toast.error('Erreur de synchronisation');
        } finally {
            setSyncing(false);
        }
    };

    const saveNotificationSettings = async () => {
        setNotifSaving(true);
        try {
            await notificationAPI.updateSettings(notifSettings);
            toast.success('Paramètres de notification sauvegardés');
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setNotifSaving(false);
        }
    };

    const updateVendorPriority = (vendorId, newPriority) => {
        setVendors(vendors.map(v => 
            v.id === vendorId ? { ...v, notification_priority: newPriority } : v
        ));
    };

    const moveVendorUp = (index) => {
        if (index === 0) return;
        const newVendors = [...vendors];
        // Swap priorities
        const temp = newVendors[index].notification_priority;
        newVendors[index].notification_priority = newVendors[index - 1].notification_priority;
        newVendors[index - 1].notification_priority = temp;
        // Swap positions
        [newVendors[index], newVendors[index - 1]] = [newVendors[index - 1], newVendors[index]];
        setVendors(newVendors);
    };

    const moveVendorDown = (index) => {
        if (index === vendors.length - 1) return;
        const newVendors = [...vendors];
        // Swap priorities
        const temp = newVendors[index].notification_priority;
        newVendors[index].notification_priority = newVendors[index + 1].notification_priority;
        newVendors[index + 1].notification_priority = temp;
        // Swap positions
        [newVendors[index], newVendors[index + 1]] = [newVendors[index + 1], newVendors[index]];
        setVendors(newVendors);
    };

    const saveVendorPriorities = async () => {
        setSavingPriorities(true);
        try {
            const priorities = vendors.map((v, index) => ({
                vendor_id: v.id,
                priority: index + 1
            }));
            await notificationAPI.updateVendorsPriorityBulk(priorities);
            toast.success('Priorités des vendeurs sauvegardées');
            fetchVendorsPriority(); // Refresh
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSavingPriorities(false);
        }
    };

    const processQueue = async () => {
        setProcessingQueue(true);
        try {
            const response = await notificationAPI.processQueue();
            toast.success(`File traitée: ${response.data.processed || 0} notifications envoyées`);
            fetchQueue();
        } catch (error) {
            toast.error('Erreur lors du traitement');
        } finally {
            setProcessingQueue(false);
        }
    };

    const sendTestTelegram = async () => {
        const adminChatId = notifSettings.admin_telegram_chat_id;
        if (!adminChatId) {
            toast.error('Veuillez configurer votre Chat ID Admin Telegram');
            return;
        }
        setTestingTelegram(true);
        try {
            const response = await api.post('/telegram/test', { chat_id: adminChatId });
            if (response.data.success) {
                toast.success('Message de test envoyé avec succès!');
            } else {
                toast.error(`Échec: ${response.data.error || 'Erreur inconnue'}`);
            }
        } catch (error) {
            toast.error('Erreur lors de l\'envoi du test');
        } finally {
            setTestingTelegram(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copié dans le presse-papier');
    };

    const delayOptions = [
        { value: 2, label: '2 minutes' },
        { value: 5, label: '5 minutes' },
        { value: 10, label: '10 minutes' },
        { value: 15, label: '15 minutes' },
        { value: 30, label: '30 minutes' },
        { value: 60, label: '1 heure' },
    ];

    const hourOptions = Array.from({ length: 24 }, (_, i) => ({
        value: i,
        label: `${i.toString().padStart(2, '0')}:00`
    }));

    return (
        <Layout>
            <div className="space-y-6 max-w-4xl" data-testid="settings-page">
                {/* Header */}
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Paramètres</h1>
                    <p className="text-muted-foreground mt-1">
                        Configuration et intégrations
                    </p>
                </div>

                {/* Account Info */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base">Mon Compte</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                                <p className="text-muted-foreground">Nom</p>
                                <p className="font-medium">{user?.name}</p>
                            </div>
                            <div>
                                <p className="text-muted-foreground">Email</p>
                                <p className="font-medium">{user?.email}</p>
                            </div>
                            <div>
                                <p className="text-muted-foreground">Rôle</p>
                                <Badge variant="outline" className="mt-1">
                                    {user?.role === 'super_admin' ? 'Super Admin' :
                                     user?.role === 'admin' ? 'Administrateur' :
                                     user?.role === 'vendeur' ? 'Vendeur' :
                                     user?.role === 'driver' ? 'Livreur' :
                                     user?.role === 'office_agent' ? 'Agent' : user?.role}
                                </Badge>
                            </div>
                            <div>
                                <p className="text-muted-foreground">Organisation</p>
                                <p className="font-medium">{user?.organization_name || '-'}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* My Notifications - For ALL users */}
                <MyNotificationsSection user={user} />

                {/* Push Notifications - For ALL users */}
                <PushNotificationsCard />

                {/* Notification Tone Settings - Admin Only */}
                {isAdmin && <NotificationToneSettings />}

                {/* Reassignment Chain Settings - Admin Only */}
                {isAdmin && <ReassignmentChainSettings />}

                {/* Notification Escalation Settings - Admin Only */}
                {isAdmin && (
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Bell className="w-5 h-5 text-blue-500" />
                                Notifications - Escalade
                            </CardTitle>
                            <CardDescription>
                                Configuration du système de notification en escalade (Telegram)
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {notifLoading ? (
                                <div className="flex items-center justify-center py-4">
                                    <Loader2 className="w-6 h-6 animate-spin" />
                                </div>
                            ) : (
                                <>
                                    {/* Telegram Bot Status */}
                                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="text-2xl">📱</div>
                                                <div>
                                                    <h4 className="font-medium text-blue-800">Bot Telegram</h4>
                                                    {telegramLoading ? (
                                                        <p className="text-sm text-blue-600">Vérification...</p>
                                                    ) : telegramStatus?.success ? (
                                                        <p className="text-sm text-blue-600">
                                                            ✅ Connecté: @{telegramStatus.bot_username}
                                                        </p>
                                                    ) : (
                                                        <p className="text-sm text-red-600">
                                                            ❌ Non configuré
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {telegramStatus?.success && (
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm" 
                                                        onClick={sendTestTelegram}
                                                        disabled={testingTelegram || !notifSettings.admin_telegram_chat_id}
                                                    >
                                                        {testingTelegram ? (
                                                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                                        ) : (
                                                            <Bell className="w-4 h-4 mr-1" />
                                                        )}
                                                        Tester
                                                    </Button>
                                                )}
                                                {telegramStatus?.success && (
                                                    <Badge className="bg-green-100 text-green-700">Actif</Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Escalation Toggle */}
                                    <div className="p-4 rounded-lg border bg-muted/50">
                                        <div className="flex items-center justify-between">
                                            <div className="space-y-1">
                                                <Label className="flex items-center gap-2 text-base font-medium">
                                                    ⚡ Mode Escalation
                                                </Label>
                                                <p className="text-sm text-muted-foreground">
                                                    {notifSettings.escalation_enabled !== false
                                                        ? "Notifie les vendeurs un par un selon leur priorité"
                                                        : "Notifie TOUS les vendeurs en même temps"
                                                    }
                                                </p>
                                            </div>
                                            <Switch
                                                checked={notifSettings.escalation_enabled !== false}
                                                onCheckedChange={(checked) => setNotifSettings({...notifSettings, escalation_enabled: checked})}
                                            />
                                        </div>
                                        {notifSettings.escalation_enabled === false && (
                                            <div className="mt-3 p-2 bg-blue-50 rounded text-sm text-blue-700">
                                                💡 Mode simple activé : Toutes les commandes seront envoyées à tous les vendeurs simultanément.
                                            </div>
                                        )}
                                    </div>

                                    {/* Escalation Settings - Only show when escalation is enabled */}
                                    {notifSettings.escalation_enabled !== false && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-2">
                                                <Clock className="w-4 h-4" />
                                                Délai entre vendeurs
                                            </Label>
                                            <Select
                                                value={notifSettings.escalation_delay_minutes?.toString()}
                                                onValueChange={(v) => setNotifSettings({...notifSettings, escalation_delay_minutes: parseInt(v)})}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Sélectionner..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {delayOptions.map(opt => (
                                                        <SelectItem key={opt.value} value={opt.value.toString()}>
                                                            {opt.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <p className="text-xs text-muted-foreground">
                                                Temps d&apos;attente avant de notifier le vendeur suivant
                                            </p>
                                        </div>

                                        <div className="space-y-2">
                                            <Label className="flex items-center gap-2">
                                                📱 Telegram Admin (Chat ID)
                                            </Label>
                                            <Input
                                                placeholder="123456789"
                                                value={notifSettings.admin_telegram_chat_id || ''}
                                                onChange={(e) => setNotifSettings({...notifSettings, admin_telegram_chat_id: e.target.value})}
                                            />
                                            <p className="text-xs text-muted-foreground">
                                                Pour alertes si aucun vendeur ne claim
                                            </p>
                                        </div>
                                    </div>
                                    )}

                                    {/* Night Mode Settings - Only show when escalation is enabled */}
                                    {notifSettings.escalation_enabled !== false && (
                                    <div className="p-4 bg-slate-50 rounded-lg border">
                                        <div className="flex items-center justify-between mb-4">
                                            <div>
                                                <h4 className="font-medium flex items-center gap-2">
                                                    <Moon className="w-4 h-4" />
                                                    Mode Nuit (Algeria Time)
                                                </h4>
                                                <p className="text-sm text-muted-foreground">
                                                    {notifSettings.night_mode_enabled !== false 
                                                        ? "Les commandes reçues la nuit seront notifiées à 6h du matin"
                                                        : "Mode nuit désactivé - notifications 24h/24"
                                                    }
                                                </p>
                                            </div>
                                            <Switch
                                                checked={notifSettings.night_mode_enabled !== false}
                                                onCheckedChange={(checked) => setNotifSettings({...notifSettings, night_mode_enabled: checked})}
                                            />
                                        </div>
                                        {notifSettings.night_mode_enabled !== false && (
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Début (Nuit)</Label>
                                                <Select
                                                    value={notifSettings.night_start_hour?.toString()}
                                                    onValueChange={(v) => setNotifSettings({...notifSettings, night_start_hour: parseInt(v)})}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {hourOptions.map(opt => (
                                                            <SelectItem key={opt.value} value={opt.value.toString()}>
                                                                {opt.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Fin (Matin)</Label>
                                                <Select
                                                    value={notifSettings.night_end_hour?.toString()}
                                                    onValueChange={(v) => setNotifSettings({...notifSettings, night_end_hour: parseInt(v)})}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {hourOptions.map(opt => (
                                                            <SelectItem key={opt.value} value={opt.value.toString()}>
                                                                {opt.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        </div>
                                        )}
                                    </div>
                                    )}

                                    {/* Notify Admin Toggle - Only show when escalation is enabled */}
                                    {notifSettings.escalation_enabled !== false && (
                                    <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                                        <div>
                                            <p className="font-medium">Notifier l&apos;admin si aucun claim</p>
                                            <p className="text-sm text-muted-foreground">
                                                Envoyer une alerte Telegram à l&apos;admin après tous les vendeurs
                                            </p>
                                        </div>
                                        <Switch
                                            checked={notifSettings.notify_admin_after_all}
                                            onCheckedChange={(v) => setNotifSettings({...notifSettings, notify_admin_after_all: v})}
                                        />
                                    </div>
                                    )}

                                    {/* SMS Confirmation System Toggle */}
                                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <div>
                                                <h4 className="font-medium flex items-center gap-2">
                                                    📱 Confirmation SMS Client
                                                </h4>
                                                <p className="text-sm text-muted-foreground">
                                                    {notifSettings.sms_confirmation_enabled !== false 
                                                        ? "Les clients reçoivent un SMS pour confirmer leur commande"
                                                        : "Désactivé - Aucun SMS envoyé aux clients"
                                                    }
                                                </p>
                                            </div>
                                            <Switch
                                                checked={notifSettings.sms_confirmation_enabled !== false}
                                                onCheckedChange={(checked) => setNotifSettings({...notifSettings, sms_confirmation_enabled: checked})}
                                            />
                                        </div>
                                        {notifSettings.sms_confirmation_enabled !== false && (
                                            <div className="mt-3 p-3 bg-white rounded border text-sm">
                                                <p className="font-medium mb-2">📋 Nouveau flux simplifié :</p>
                                                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                                                    <li>📨 <strong>1 seul SMS</strong> envoyé au client avec lien de confirmation</li>
                                                    <li>✅ Client confirme ou ❌ annule via le lien</li>
                                                    <li>👤 Admin/Confirmateur assigne manuellement la commande à un vendeur</li>
                                                    <li>📱 Vendeur reçoit notification Telegram avec tous les détails</li>
                                                </ol>
                                                <p className="mt-2 text-xs text-green-700 font-medium">
                                                    💰 Optimisé pour réduire les coûts SMS (1 SMS par commande)
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    <Button onClick={saveNotificationSettings} disabled={notifSaving}>
                                        {notifSaving ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : (
                                            <Save className="w-4 h-4 mr-2" />
                                        )}
                                        Sauvegarder les paramètres
                                    </Button>
                                </>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Vendor Priority - Admin Only */}
                {isAdmin && (
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base flex items-center gap-2">
                                <Users className="w-5 h-5 text-blue-500" />
                                Priorité des Vendeurs
                            </CardTitle>
                            <CardDescription>
                                Ordre dans lequel les vendeurs reçoivent les notifications (1 = premier)
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            {vendorsLoading ? (
                                <div className="flex items-center justify-center py-4">
                                    <Loader2 className="w-6 h-6 animate-spin" />
                                </div>
                            ) : vendors.length === 0 ? (
                                <p className="text-muted-foreground text-center py-4">
                                    Aucun vendeur configuré
                                </p>
                            ) : (
                                <>
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className="w-16">Ordre</TableHead>
                                                <TableHead>Vendeur</TableHead>
                                                <TableHead>📱 Telegram</TableHead>
                                                <TableHead className="w-24">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {vendors.map((vendor, index) => (
                                                <TableRow key={vendor.id}>
                                                    <TableCell>
                                                        <Badge variant="outline" className="font-bold">
                                                            #{index + 1}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="font-medium">
                                                        {vendor.name}
                                                    </TableCell>
                                                    <TableCell className="text-sm">
                                                        {vendor.telegram_chat_id ? (
                                                            <span className="text-blue-600">{vendor.telegram_chat_id}</span>
                                                        ) : (
                                                            <span className="text-muted-foreground">-</span>
                                                        )}
                                                        {vendor.telegram_notifications_enabled !== false && vendor.telegram_chat_id && (
                                                            <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">ON</Badge>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <div className="flex gap-1">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8"
                                                                onClick={() => moveVendorUp(index)}
                                                                disabled={index === 0}
                                                            >
                                                                <ArrowUp className="w-4 h-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-8 w-8"
                                                                onClick={() => moveVendorDown(index)}
                                                                disabled={index === vendors.length - 1}
                                                            >
                                                                <ArrowDown className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    
                                    <div className="mt-4">
                                        <Button onClick={saveVendorPriorities} disabled={savingPriorities}>
                                            {savingPriorities ? (
                                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            ) : (
                                                <Save className="w-4 h-4 mr-2" />
                                            )}
                                            Sauvegarder l&apos;ordre
                                        </Button>
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Notification Queue - Admin Only */}
                {isAdmin && (
                    <Card>
                        <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Clock className="w-5 h-5 text-purple-500" />
                                        File d&apos;attente des Notifications
                                    </CardTitle>
                                    <CardDescription>
                                        Commandes en attente d&apos;escalade
                                    </CardDescription>
                                </div>
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" onClick={fetchQueue} disabled={queueLoading}>
                                        <RefreshCw className={`w-4 h-4 mr-1 ${queueLoading ? 'animate-spin' : ''}`} />
                                        Actualiser
                                    </Button>
                                    <Button size="sm" onClick={processQueue} disabled={processingQueue}>
                                        {processingQueue ? (
                                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                        ) : (
                                            <Play className="w-4 h-4 mr-1" />
                                        )}
                                        Traiter maintenant
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {queue.length === 0 ? (
                                <p className="text-muted-foreground text-center py-4">
                                    Aucune notification en attente
                                </p>
                            ) : (
                                <div className="space-y-2">
                                    {queue.map(item => (
                                        <div key={item.order_id} className="p-3 bg-slate-50 rounded-lg border flex items-center justify-between">
                                            <div>
                                                <p className="font-medium">{item.n_commande}</p>
                                                <p className="text-sm text-muted-foreground">
                                                    Vendeur actuel: {item.current_vendor_name || '-'} 
                                                    <span className="mx-2">•</span>
                                                    Statut: <Badge variant="outline">{item.status}</Badge>
                                                </p>
                                                {item.next_notification_at && (
                                                    <p className="text-xs text-muted-foreground">
                                                        Prochaine notification: {new Date(item.next_notification_at).toLocaleString('fr-FR')}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Shopify Integration - Admin Only */}
                {isAdmin && (
                    <>
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <ShoppingBag className="w-5 h-5" />
                                    Shopify
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                {shopifyLoading ? (
                                    <div className="flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Vérification...
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            {shopifyStatus.configured ? (
                                                <CheckCircle className="w-5 h-5 text-green-500" />
                                            ) : (
                                                <XCircle className="w-5 h-5 text-red-500" />
                                            )}
                                            <div>
                                                <p className="font-medium">
                                                    {shopifyStatus.configured ? 'Connecté' : 'Non configuré'}
                                                </p>
                                                {shopifyStatus.shop_url && (
                                                    <p className="text-sm text-muted-foreground">{shopifyStatus.shop_url}</p>
                                                )}
                                            </div>
                                        </div>
                                        {shopifyStatus.configured && (
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={handleShopifySync}
                                                disabled={syncing}
                                            >
                                                {syncing ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="w-4 h-4 mr-2" />
                                                )}
                                                Synchroniser
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Zapier Integration */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-base flex items-center gap-2">
                                    <Webhook className="w-5 h-5 text-orange-500" />
                                    Zapier - Webhook Commandes
                                </CardTitle>
                                <CardDescription>
                                    URL pour recevoir automatiquement les nouvelles commandes Shopify
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex items-center gap-2">
                                    <Input 
                                        value={webhookUrl} 
                                        readOnly 
                                        className="font-mono text-sm"
                                    />
                                    <Button 
                                        variant="outline" 
                                        size="icon"
                                        onClick={() => copyToClipboard(webhookUrl)}
                                    >
                                        <Copy className="w-4 h-4" />
                                    </Button>
                                </div>

                                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                                    <h4 className="font-medium text-orange-800 mb-2">Configuration Zapier</h4>
                                    <ol className="text-sm text-orange-700 space-y-2 list-decimal list-inside">
                                        <li><strong>Trigger:</strong> Shopify → &quot;New Order&quot;</li>
                                        <li><strong>Action:</strong> Webhooks by Zapier → &quot;POST&quot;</li>
                                        <li><strong>URL:</strong> Copiez l&apos;URL ci-dessus</li>
                                        <li><strong>Mappez les champs requis:</strong>
                                            <div className="ml-5 mt-2 space-y-1.5">
                                                <div className="grid grid-cols-2 gap-2 text-xs">
                                                    <div className="font-semibold">Champ Zapier:</div>
                                                    <div className="font-semibold">Valeur Shopify:</div>
                                                    
                                                    <code className="bg-orange-100 px-1 rounded">order_number</code>
                                                    <span>Order Number</span>
                                                    
                                                    <code className="bg-orange-100 px-1 rounded">full_name</code>
                                                    <span>Customer: Name</span>
                                                    
                                                    <code className="bg-orange-100 px-1 rounded">phone</code>
                                                    <span>Shipping: Phone</span>
                                                    
                                                    <code className="bg-orange-100 px-1 rounded">address_1</code>
                                                    <span>Shipping: Address 1</span>
                                                    
                                                    <code className="bg-orange-100 px-1 rounded">wilaya</code>
                                                    <span>Shipping: Province</span>
                                                    
                                                    <code className="bg-orange-100 px-1 rounded">product_name</code>
                                                    <span>Line Items: Name</span>
                                                    
                                                    <code className="bg-orange-100 px-1 rounded">product_price</code>
                                                    <span>Subtotal Price</span>
                                                    
                                                    <code className="bg-orange-100 px-1 rounded">shipping_price</code>
                                                    <span>Shipping: Price</span>
                                                    
                                                    <code className="bg-orange-100 px-1 rounded">total_price</code>
                                                    <span>Total Price</span>
                                                </div>
                                            </div>
                                        </li>
                                        <li><strong>Testez</strong> votre Zap avec une commande test Shopify</li>
                                    </ol>
                                    <p className="text-xs text-orange-600 mt-3">
                                        💡 <strong>Note:</strong> Les commandes créées déclenchent automatiquement le système d&apos;escalade des notifications.
                                    </p>
                                </div>

                                <a 
                                    href="https://zapier.com/app/zaps"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                                >
                                    Ouvrir Zapier
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                            </CardContent>
                        </Card>
                    </>
                )}
            </div>
        </Layout>
    );
}
